class KatyTheme {
  constructor() {
    this.trackingInitialSent = false;
    this.bindCookieConsent();
    this.bindAnalyticsDataLayer();
    this.bindQuantity();
    this.bindGallery();
    this.bindProductLightbox();
    this.bindCopyButtons();
    this.bindHeroSliders();
    this.bindScrollButtons();
    this.bindProductCardSwatches();
    this.bindPredictiveSearch();
    this.bindProductVariantPicker();
    this.bindMobileGallery();
    this.bindProductModals();
    this.bindProductTabs();
    this.bindProductStickyBuy();
    this.bindProductInfoColumn();
    this.bindArrival();
    this.bindVideoRow();
    this.bindShippingCalculators();
    this.bindCartRemoval();
    this.bindCartUpsell();
    this.bindCartPage();
    this.bindCartCountdown();
    this.bindCartTimer();
    this.bindMobileNav();
    this.bindCollectionSort();
  }

  getJSONData(selector) {
    const script = document.querySelector(selector);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (_error) {
      return null;
    }
  }

  getTrackingConfig() {
    return this.getJSONData('[data-katy-tracking-config]') || {};
  }

  loadScriptOnce(id, src, onload) {
    if (document.getElementById(id)) {
      if (onload) onload();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.async = true;
    script.src = src;
    if (onload) script.addEventListener('load', onload, { once: true });
    document.head.appendChild(script);
  }

  loadShopifyPrivacyApi(callback) {
    if (window.Shopify && window.Shopify.customerPrivacy) {
      callback();
      return;
    }

    if (!window.Shopify || !window.Shopify.loadFeatures) {
      callback();
      return;
    }

    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      () => callback()
    );
  }

  normalizeConsentValue(value) {
    return value === true || value === 'yes' || value === 'true' || value === '1';
  }

  getStoredConsent() {
    try {
      const stored = window.localStorage.getItem('katy_cookie_consent_v1');
      if (stored) return JSON.parse(stored);
    } catch (_error) {
      // localStorage can be blocked by the browser.
    }

    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || !privacy.currentVisitorConsent) return null;

    const current = privacy.currentVisitorConsent();
    if (!current) return null;

    const hasChoice = ['analytics', 'marketing', 'preferences'].some((key) =>
      ['yes', 'no', true, false, 'true', 'false', '1', '0'].includes(current[key])
    );

    if (!hasChoice) return null;

    return {
      analytics: this.normalizeConsentValue(current.analytics),
      marketing: this.normalizeConsentValue(current.marketing),
      preferences: this.normalizeConsentValue(current.preferences),
    };
  }

  saveTrackingConsent(consent) {
    const payload = {
      analytics: Boolean(consent.analytics),
      marketing: Boolean(consent.marketing),
      preferences: Boolean(consent.preferences),
      savedAt: new Date().toISOString(),
    };

    try {
      window.localStorage.setItem('katy_cookie_consent_v1', JSON.stringify(payload));
    } catch (_error) {
      // Consent is still sent to Shopify when the API is available.
    }

    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && privacy.setTrackingConsent) {
      privacy.setTrackingConsent(
        {
          analytics: payload.analytics,
          marketing: payload.marketing,
          preferences: payload.preferences,
        },
        () => {}
      );
    }

    document.dispatchEvent(new CustomEvent('katy:consent-updated', { detail: payload }));
  }

  hasTrackingConsent(purpose) {
    const consent = this.getStoredConsent();
    return Boolean(consent && consent[purpose]);
  }

  bindCookieConsent() {
    const banner = document.querySelector('[data-cookie-consent]');
    if (!banner) return;

    const accept = banner.querySelector('[data-cookie-accept]');
    const decline = banner.querySelector('[data-cookie-decline]');

    const syncVisibility = () => {
      banner.hidden = Boolean(this.getStoredConsent());
    };

    if (accept) {
      accept.addEventListener('click', () => {
        this.saveTrackingConsent({ analytics: true, marketing: true, preferences: true });
        banner.hidden = true;
      });
    }

    if (decline) {
      decline.addEventListener('click', () => {
        this.saveTrackingConsent({ analytics: false, marketing: false, preferences: false });
        banner.hidden = true;
      });
    }

    this.loadShopifyPrivacyApi(syncVisibility);
  }

  centsToValue(cents) {
    return Math.round(Number(cents || 0)) / 100;
  }

  normalizeAnalyticsItem(item) {
    return {
      item_id: String(item.item_variant || item.item_id || ''),
      item_name: item.item_name || '',
      item_brand: item.item_brand || '',
      item_category: item.item_category || '',
      item_variant: item.variant_title || String(item.item_variant || ''),
      price: this.centsToValue(item.price_cents),
      quantity: Number(item.quantity || 1),
    };
  }

  pushTrackingEvent(eventName, ecommerce = {}) {
    const config = this.getTrackingConfig();
    if (!config.enabled) return;

    const analyticsAllowed = this.hasTrackingConsent('analytics');
    const marketingAllowed = this.hasTrackingConsent('marketing');
    if (!analyticsAllowed && !marketingAllowed) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ecommerce,
    });

    if (analyticsAllowed && typeof window.gtag === 'function') {
      window.gtag('event', eventName, ecommerce);
    }

  }

  loadTrackingScripts() {
    const config = this.getTrackingConfig();
    if (!config.enabled) return;

    const analyticsAllowed = this.hasTrackingConsent('analytics');
    const marketingAllowed = this.hasTrackingConsent('marketing');
    const gtmId = String(config.gtmContainerId || '').trim();
    const googleTagId = String(config.googleTagId || '').trim();
    const safeGoogleId = /^[A-Z0-9-]+$/i;

    window.dataLayer = window.dataLayer || [];

    if ((analyticsAllowed || marketingAllowed) && gtmId && /^GTM-[A-Z0-9]+$/i.test(gtmId)) {
      this.loadScriptOnce('katy-gtm', `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`);
    }

    if (analyticsAllowed && googleTagId && safeGoogleId.test(googleTagId)) {
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
      window.gtag('consent', 'default', {
        analytics_storage: 'granted',
        ad_storage: marketingAllowed ? 'granted' : 'denied',
        ad_user_data: marketingAllowed ? 'granted' : 'denied',
        ad_personalization: marketingAllowed ? 'granted' : 'denied',
      });
      window.gtag('js', new Date());
      window.gtag('config', googleTagId, { send_page_view: false });
      this.loadScriptOnce('katy-google-tag', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleTagId)}`);
    }

  }

  bindAnalyticsDataLayer() {
    const config = this.getTrackingConfig();
    if (!config.enabled) return;

    const pageEvent = () => {
      if (this.trackingInitialSent) return;
      this.loadTrackingScripts();

      this.pushTrackingEvent('page_view', {
        page_title: document.title,
        page_path: window.location.pathname,
        template: config.template,
        shop: config.shop,
      });

      const product = this.getJSONData('[data-product-analytics]');
      if (product) {
        const item = this.normalizeAnalyticsItem(product);
        this.pushTrackingEvent('view_item', {
          currency: product.currency || config.currency,
          value: item.price,
          items: [item],
        });
      }

      const cart = this.getJSONData('[data-cart-analytics]');
      if (cart) {
        this.pushTrackingEvent('view_cart', {
          currency: cart.currency || config.currency,
          value: this.centsToValue(cart.value_cents),
          items: (cart.items || []).map((item) => this.normalizeAnalyticsItem(item)),
        });
      }

      this.trackingInitialSent = true;
    };

    this.loadShopifyPrivacyApi(pageEvent);
    document.addEventListener('katy:consent-updated', pageEvent);

    document.querySelectorAll('[data-analytics-product] a').forEach((link) => {
      link.addEventListener('click', () => {
        const card = link.closest('[data-analytics-product]');
        if (!card) return;
        const item = this.normalizeAnalyticsItem({
          item_id: card.dataset.productId,
          item_name: card.dataset.productTitle,
          item_brand: card.dataset.productVendor,
          item_category: card.dataset.productType,
          price_cents: card.dataset.productPriceCents,
        });
        this.pushTrackingEvent('select_item', {
          currency: config.currency,
          value: item.price,
          items: [item],
        });
      });
    });

    document.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.addEventListener('click', () => {
        const product = this.getJSONData('[data-product-analytics]');
        if (!product) return;
        const item = this.normalizeAnalyticsItem({
          ...product,
          item_variant: button.dataset.variantId || product.item_variant,
        });
        this.pushTrackingEvent('select_lens_flow', {
          currency: product.currency || config.currency,
          value: item.price,
          items: [item],
        });
      });
    });

    // V78: escuta na janela, em captura. O app de lentes intercepta o clique de finalizar antes
    // de ele chegar ao botão, e o evento begin_checkout nunca era enviado no carrinho.
    if (document.querySelector('button[name="checkout"]')) {
      window.addEventListener(
        'click',
        (event) => {
          const target = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
          if (!target || target.disabled) return;
          const cart = this.getJSONData('[data-cart-analytics]');
          if (!cart) return;
          this.pushTrackingEvent('begin_checkout', {
            currency: cart.currency || config.currency,
            value: this.centsToValue(cart.value_cents),
            items: (cart.items || []).map((item) => this.normalizeAnalyticsItem(item)),
          });
        },
        true,
      );
    }

    document.querySelectorAll('form[action*="/search"]').forEach((form) => {
      form.addEventListener('submit', () => {
        const input = form.querySelector('input[name="q"]');
        if (!input || !input.value.trim()) return;
        this.pushTrackingEvent('search', {
          search_term: input.value.trim().slice(0, 120),
        });
      });
    });
  }

  bindQuantity() {
    document.querySelectorAll('[data-quantity]').forEach((wrapper) => {
      const input = wrapper.querySelector('input');
      wrapper.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const change = button.dataset.quantityButton === 'plus' ? 1 : -1;
          const next = Math.max(1, Number(input.value || 1) + change);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  bindGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-gallery-main]');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          const image = button.querySelector('img');
          if (!main || !image) return;
          main.src = image.dataset.large || image.src;
          main.alt = image.alt;
        });
      });
    });
  }

  bindProductLightbox() {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const imageNodes = Array.from(gallery.querySelectorAll(
      '.product-gallery__desktop-item img, [data-gallery-main], .product-gallery__mobile-slide img'
    ));

    const images = [];
    imageNodes.forEach((img) => {
      const src = img.dataset.large || img.currentSrc || img.src;
      if (!src) return;
      if (images.some((entry) => entry.src === src)) return;
      images.push({ src, alt: img.alt || 'Imagem do produto' });
      img.setAttribute('data-product-lightbox-image', String(images.length - 1));
    });

    if (!images.length) return;

    const lightbox = document.createElement('div');
    lightbox.className = 'product-lightbox';
    lightbox.hidden = true;
    lightbox.innerHTML = `
      <div class="product-lightbox__overlay" data-product-lightbox-close></div>
      <div class="product-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Imagem ampliada do produto">
        <button class="product-lightbox__close" type="button" data-product-lightbox-close aria-label="Fechar">×</button>
        <button class="product-lightbox__arrow product-lightbox__arrow--prev" type="button" data-product-lightbox-prev aria-label="Imagem anterior">‹</button>
        <img class="product-lightbox__image" alt="">
        <button class="product-lightbox__arrow product-lightbox__arrow--next" type="button" data-product-lightbox-next aria-label="Próxima imagem">›</button>
      </div>
    `;
    document.body.appendChild(lightbox);

    const lightboxImage = lightbox.querySelector('.product-lightbox__image');
    const prev = lightbox.querySelector('[data-product-lightbox-prev]');
    const next = lightbox.querySelector('[data-product-lightbox-next]');
    let activeIndex = 0;

    const render = () => {
      const image = images[activeIndex];
      if (!image || !lightboxImage) return;
      lightboxImage.src = image.src;
      lightboxImage.alt = image.alt;
      lightbox.classList.toggle('product-lightbox--single', images.length < 2);
    };

    const open = (index) => {
      activeIndex = Math.max(0, Math.min(images.length - 1, Number(index) || 0));
      render();
      lightbox.hidden = false;
      document.body.classList.add('has-product-lightbox');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      lightbox.hidden = true;
      document.body.classList.remove('has-product-lightbox');
      document.body.style.overflow = '';
    };

    const move = (direction) => {
      activeIndex = (activeIndex + direction + images.length) % images.length;
      render();
    };

    gallery.addEventListener('click', (event) => {
      const img = event.target.closest('[data-product-lightbox-image]');
      if (!img || event.target.closest('.product-gallery__thumb')) return;
      open(img.dataset.productLightboxImage);
    });

    lightbox.querySelectorAll('[data-product-lightbox-close]').forEach((button) => {
      button.addEventListener('click', close);
    });
    if (prev) prev.addEventListener('click', () => move(-1));
    if (next) next.addEventListener('click', () => move(1));

    document.addEventListener('keydown', (event) => {
      if (lightbox.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    });
  }

  bindCopyButtons() {
    document.querySelectorAll('[data-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        const text = button.dataset.copy;
        try {
          await navigator.clipboard.writeText(text);
          button.textContent = 'Copiado';
          setTimeout(() => (button.textContent = 'Copiar'), 1800);
        } catch (_error) {
          button.textContent = text;
        }
      });
    });
  }

  bindHeroSliders() {
    document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
      const viewport = slider.querySelector('.hero-slider__viewport');
      const slides = Array.from(slider.querySelectorAll('[data-hero-slide]'));
      const buttons = Array.from(slider.querySelectorAll('[data-hero-button]'));
      if (!slides.length) return;

      let activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (activeIndex < 0) activeIndex = 0;
      let timer = null;
      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      let suppressClick = false;
      let pointerClickHandled = false;

      const setActive = (index) => {
        const nextIndex = (index + slides.length) % slides.length;
        activeIndex = nextIndex;
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === nextIndex));
        buttons.forEach((button, buttonIndex) => button.classList.toggle('is-active', buttonIndex === nextIndex));
      };

      const getActiveSlide = () => slides[activeIndex] || slides.find((slide) => slide.classList.contains('is-active'));

      const normalizeHeroLink = (link) => {
        const value = String(link || '').trim();
        if (!value || value === '#') return '';
        return value;
      };

      const openActiveSlideLink = () => {
        const slide = getActiveSlide();
        const link = normalizeHeroLink(slide ? slide.dataset.heroLink : '');
        if (!link) return false;

        try {
          window.location.assign(link);
        } catch (_error) {
          window.location.href = link;
        }

        return true;
      };

      const stopAutoplay = () => {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          setActive(Number(button.dataset.heroButton || 0));
          stopAutoplay();
        });
      });

      slider.addEventListener('click', (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
          return;
        }

        if (pointerClickHandled) {
          event.preventDefault();
          event.stopPropagation();
          pointerClickHandled = false;
          return;
        }

        if (event.target.closest('[data-hero-button], .hero__controls, input, select, textarea, label, summary')) return;

        const slide = event.target.closest('[data-hero-slide]') || getActiveSlide();
        if (!slide || !slide.classList.contains('is-active')) return;

        const link = normalizeHeroLink(slide.dataset.heroLink);
        if (!link) return;

        event.preventDefault();
        event.stopPropagation();
        window.location.assign(link);
      }, true);

      if (viewport && slides.length > 1) {
        viewport.addEventListener('pointerdown', (event) => {
          if (event.button && event.button !== 0) return;
          isDragging = true;
          startX = event.clientX;
          currentX = event.clientX;
          viewport.classList.add('is-dragging');
          if (viewport.setPointerCapture) viewport.setPointerCapture(event.pointerId);
        });

        viewport.addEventListener('pointermove', (event) => {
          if (!isDragging) return;
          currentX = event.clientX;
        });

        const finishDrag = () => {
          if (!isDragging) return;
          const deltaX = currentX - startX;
          isDragging = false;
          viewport.classList.remove('is-dragging');

          if (Math.abs(deltaX) > 45) {
            setActive(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
            stopAutoplay();
            suppressClick = true;
            window.setTimeout(() => { suppressClick = false; }, 120);
            return;
          }

          if (Math.abs(deltaX) < 8 && openActiveSlideLink()) {
            pointerClickHandled = true;
            window.setTimeout(() => { pointerClickHandled = false; }, 180);
          }
        };

        viewport.addEventListener('pointerup', finishDrag);
        viewport.addEventListener('pointercancel', finishDrag);
        viewport.addEventListener('pointerleave', finishDrag);
      }

      if (slider.dataset.autoplay === 'true' && slides.length > 1) {
        timer = setInterval(() => {
          setActive(activeIndex + 1);
        }, 5500);
      }
    });
  }

  bindScrollButtons() {
    document.querySelectorAll('[data-scroll-next]').forEach((button) => {
      button.addEventListener('click', () => {
        const section = button.closest('.section');
        const row = section ? section.querySelector('[data-scroll-row]') : null;
        if (!row) return;

        const amount = Math.max(row.clientWidth * 0.78, 220);
        row.scrollBy({ left: amount, behavior: 'smooth' });
      });
    });
  }

  bindProductCardSwatches() {
    document.querySelectorAll('[data-product-card]').forEach((card) => {
      const image = card.querySelector('[data-card-image]');
      const links = card.querySelectorAll('[data-card-link]');
      const price = card.querySelector('[data-card-price-current]');
      const priceCurrent = card.querySelector('.price__current');
      const compareRow = card.querySelector('[data-card-compare-row]');
      const discount = card.querySelector('[data-card-discount]');
      const installment = card.querySelector('[data-card-installment]');
      const priceDiscount = card.querySelector('[data-card-price-discount]');

      card.querySelectorAll('[data-card-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => {
          card.querySelectorAll('[data-card-swatch]').forEach((item) => item.classList.remove('is-active'));
          swatch.classList.add('is-active');

          if (image && swatch.dataset.image) {
            image.src = swatch.dataset.image;
            image.removeAttribute('srcset');
          }

          if (swatch.dataset.url) {
            links.forEach((link) => {
              link.href = swatch.dataset.url;
            });
          }

          if (price && swatch.dataset.price) {
            price.textContent = swatch.dataset.price;
          }

          if (installment && swatch.dataset.installment) {
            installment.textContent = swatch.dataset.installment;
          }

          if (compareRow) {
            if (swatch.dataset.compare) {
              compareRow.textContent = '';
              const compare = document.createElement('s');
              compare.textContent = swatch.dataset.compare;
              compare.dataset.cardPriceCompare = '';
              compareRow.append(compare);
              compareRow.hidden = false;
              if (priceCurrent) priceCurrent.classList.remove('price__current--single');
            } else {
              compareRow.textContent = '';
              compareRow.hidden = true;
              if (priceCurrent) priceCurrent.classList.add('price__current--single');
            }
          }

          if (priceDiscount) {
            if (swatch.dataset.priceDiscount) {
              priceDiscount.textContent = swatch.dataset.priceDiscount;
              priceDiscount.hidden = false;
            } else {
              priceDiscount.textContent = '';
              priceDiscount.hidden = true;
            }
          }

          if (discount) {
            if (swatch.dataset.discount) {
              discount.textContent = swatch.dataset.discount;
              discount.hidden = false;
            } else {
              discount.textContent = '';
              discount.hidden = true;
            }
          }
        });
      });
    });
  }

  bindPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search]').forEach((form) => {
      const input = form.querySelector('[data-predictive-input]');
      const results = form.querySelector('[data-predictive-results]');
      const predictiveUrl = form.dataset.predictiveUrl || '/search/suggest';
      if (!input || !results) return;

      let timer = null;
      let controller = null;

      const closeResults = () => {
        results.hidden = true;
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      };

      const escapeHTML = (value) =>
        String(value || '').replace(/[&<>"']/g, (char) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;',
        }[char]));

      const getImage = (product) => {
        if (product.image) return product.image;
        if (product.featured_image && product.featured_image.url) return product.featured_image.url;
        if (product.featured_image && product.featured_image.src) return product.featured_image.src;
        return '';
      };

      const getPrice = (product) => product.price || product.price_min || product.price_max || '';

      // V75: lentes avulsas, upgrades e brindes do app de lentes não são produtos de vitrine.
      const isStorefrontProduct = (product) => {
        const type = String(product.type || '').toLowerCase();
        const title = String(product.title || '').toLowerCase().trim();
        const tags = Array.isArray(product.tags) ? product.tags.map((tag) => String(tag).toLowerCase()) : [];
        if (type.includes('lente') || type === 'addon') return false;
        if (tags.includes('lens_product') || tags.includes('hidden_lens')) return false;
        if (/^(brinde|upgrade)\b/.test(title) || title === 'produto teste') return false;
        return true;
      };

      const renderProducts = (products, query) => {
        if (!products.length) {
          results.innerHTML = `<p class="predictive-search__empty">Nenhum produto encontrado para "${escapeHTML(query)}".</p>`;
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          return;
        }

        results.innerHTML = `
          <ul class="predictive-search__list">
            ${products.map((product) => {
              const image = getImage(product);
              const price = getPrice(product);
              return `
                <li>
                  <a class="predictive-search__item" href="${escapeHTML(product.url)}" role="option">
                    ${image ? `<img class="predictive-search__image" src="${escapeHTML(image)}" alt="${escapeHTML(product.title)}" loading="lazy">` : '<span class="predictive-search__image"></span>'}
                    <span>
                      <span class="predictive-search__title">${escapeHTML(product.title)}</span>
                      ${price ? `<span class="predictive-search__price">${escapeHTML(price)}</span>` : ''}
                    </span>
                    <span class="predictive-search__cta">Ver</span>
                  </a>
                </li>
              `;
            }).join('')}
          </ul>
        `;
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const searchProducts = async (query) => {
        if (controller) controller.abort();
        controller = new AbortController();

        const baseUrl = predictiveUrl.endsWith('.json') ? predictiveUrl : `${predictiveUrl}.json`;
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[options][unavailable_products]', 'last');

        try {
          const response = await fetch(url.toString(), {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          });
          if (!response.ok) return;

          const data = await response.json();
          const products = data.resources && data.resources.results && data.resources.results.products
            ? data.resources.results.products
            : [];
          renderProducts(products.filter(isStorefrontProduct).slice(0, 6), query);
        } catch (error) {
          if (error.name !== 'AbortError') closeResults();
        }
      };

      input.addEventListener('input', () => {
        const query = input.value.trim();
        clearTimeout(timer);

        if (query.length < 2) {
          closeResults();
          return;
        }

        timer = setTimeout(() => searchProducts(query), 220);
      });

      input.addEventListener('focus', () => {
        const query = input.value.trim();
        if (query.length >= 2 && results.innerHTML.trim() !== '') {
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeResults();
      });

      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) closeResults();
      });
    });
  }

  bindProductVariantPicker() {
    document.querySelectorAll('[data-product-variant-picker]').forEach((form) => {
      const price = document.querySelector('[data-product-price]');
      const prefix = document.querySelector('[data-product-price-prefix]');
      const compareWrap = document.querySelector('[data-product-compare-wrap]');
      const saving = document.querySelector('[data-product-saving]');
      const installment = document.querySelector('[data-product-installment]');
      const galleryMain = document.querySelector('[data-gallery-main]');
      const galleryDesktopFeatured = document.querySelector('[data-gallery-desktop-featured]');
      const galleryMobileFeatured = document.querySelector('[data-gallery-mobile-featured]');
      const stickyPrice = document.querySelector('[data-sticky-product-price]');
      const stickyButton = document.querySelector('[data-sticky-add-to-cart]');
      const stickyLabel = document.querySelector('[data-sticky-buy-label]');
      const addToCartButton = document.querySelector('[data-add-to-cart]');
      const addToCartLabel = document.querySelector('[data-product-buy-label]');
      const addToCartSub = document.querySelector('[data-product-buy-sub]');
      const labelFor = (node, isAvailable) => {
        if (!node) return '';
        return isAvailable
          ? (node.dataset.labelAvailable || 'Escolher minhas lentes')
          : (node.dataset.labelSoldout || 'Esgotado');
      };

      form.querySelectorAll('[data-product-variant-radio]').forEach((radio) => {
        radio.addEventListener('change', () => {
          form.querySelectorAll('.variant-card').forEach((card) => card.classList.remove('is-selected'));
          const label = radio.closest('.variant-card');
          if (label) label.classList.add('is-selected');

          if (price && radio.dataset.price) price.textContent = radio.dataset.price;
          if (stickyPrice && radio.dataset.price) stickyPrice.textContent = radio.dataset.price;

          const isAvailable = radio.dataset.available !== 'false';
          if (stickyButton) stickyButton.disabled = !isAvailable;
          if (stickyLabel) stickyLabel.textContent = labelFor(stickyLabel, isAvailable);
          if (addToCartButton) {
            addToCartButton.disabled = !isAvailable;
            if (radio.dataset.variantId) addToCartButton.dataset.variantId = radio.dataset.variantId;
          }
          if (addToCartLabel) addToCartLabel.textContent = labelFor(addToCartLabel, isAvailable);
          if (addToCartSub) addToCartSub.hidden = !isAvailable;

          if (radio.dataset.compare) {
            if (prefix) {
              prefix.textContent = 'Por';
              prefix.hidden = false;
            }
            if (compareWrap) {
              compareWrap.textContent = '';
              const label = document.createElement('span');
              const compare = document.createElement('s');
              label.textContent = 'De';
              compare.textContent = radio.dataset.compare;
              compare.dataset.productCompare = '';
              compareWrap.append(label, compare);
              compareWrap.hidden = false;
            }
            if (saving) {
              saving.textContent = radio.dataset.saving;
              saving.hidden = false;
            }
          } else {
            if (prefix) {
              prefix.textContent = '';
              prefix.hidden = true;
            }
            if (compareWrap) {
              compareWrap.textContent = '';
              compareWrap.hidden = true;
            }
            if (saving) {
              saving.textContent = '';
              saving.hidden = true;
            }
          }

          if (installment && radio.dataset.installment) installment.textContent = radio.dataset.installment;

          if (galleryMain && radio.dataset.image) {
            galleryMain.src = radio.dataset.image;
            galleryMain.removeAttribute('srcset');
          }

          if (galleryDesktopFeatured && radio.dataset.image) {
            galleryDesktopFeatured.src = radio.dataset.image;
            galleryDesktopFeatured.removeAttribute('srcset');
          }

          if (galleryMobileFeatured && radio.dataset.image) {
            galleryMobileFeatured.src = radio.dataset.image;
            galleryMobileFeatured.removeAttribute('srcset');
          }
        });
      });
    });
  }

  // V76: no computador a coluna de compra acompanha a rolagem (fica "presa" ao lado das fotos).
  // Quando ela é mais alta que a tela (notebooks), o botão de compra ficava fora da área visível
  // durante toda a rolagem das fotos. Nesse caso a coluna passa a rolar normalmente com a página.
  bindProductInfoColumn() {
    const info = document.querySelector('.product-info');
    if (!info) return;
    const update = () => {
      const stickyTop = 138;
      const fits = info.offsetHeight + stickyTop + 16 <= window.innerHeight;
      info.classList.toggle('product-info--tall', !fits);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(info);
  }

  bindProductStickyBuy() {
    const sticky = document.querySelector('[data-product-sticky-buy]');
    const stickyButton = document.querySelector('[data-sticky-add-to-cart]');
    const addToCartButton = document.querySelector('[data-add-to-cart]');
    if (!sticky || !stickyButton || !addToCartButton) return;

    // V76: a barra fixa só aparece quando o botão principal não está na tela. Antes ela surgia
    // aos 420 px de rolagem e ficava duplicada ao lado do botão principal.
    const updateVisibility = () => {
      const buttonPosition = addToCartButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const buttonOnScreen = buttonPosition.bottom > 0 && buttonPosition.top < viewportHeight;
      const shouldShow = window.scrollY > 120 && !buttonOnScreen;
      sticky.hidden = !shouldShow;
      document.body.classList.toggle('has-product-sticky-buy', shouldShow);
    };

    stickyButton.addEventListener('click', () => {
      if (addToCartButton.disabled) return;
      addToCartButton.click();
    });

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    updateVisibility();
  }

  bindMobileGallery() {
    document.querySelectorAll('[data-mobile-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-mobile-gallery-track]');
      const prev = gallery.querySelector('[data-mobile-gallery-prev]');
      const next = gallery.querySelector('[data-mobile-gallery-next]');
      if (!track) return;

      const move = (direction) => {
        const amount = Math.max(track.clientWidth * 0.86, 240);
        track.scrollBy({ left: amount * direction, behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => move(-1));
      if (next) next.addEventListener('click', () => move(1));
    });
  }

  bindProductModals() {
    document.querySelectorAll('[data-modal-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const modal = document.getElementById(button.dataset.modalOpen);
        if (!modal) return;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
      });
    });

    const closeModal = (modal) => {
      modal.hidden = true;
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-product-modal]').forEach((modal) => {
      modal.querySelectorAll('[data-modal-close]').forEach((button) => {
        button.addEventListener('click', () => closeModal(modal));
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('[data-product-modal]:not([hidden])').forEach((modal) => closeModal(modal));
    });
  }

  bindProductTabs() {
    document.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
      const buttons = Array.from(tabs.querySelectorAll('[data-tab-button]'));
      const panels = Array.from(tabs.querySelectorAll('[data-tab-panel]'));

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const target = button.dataset.tabButton;

          buttons.forEach((item) => {
            const isActive = item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-selected', String(isActive));
          });

          panels.forEach((panel) => {
            const isActive = panel.dataset.tabPanel === target;
            panel.classList.toggle('is-active', isActive);
            panel.hidden = !isActive;
          });
        });
      });
    });
  }

  bindShippingCalculators() {
    const calculators = document.querySelectorAll('[data-shipping-calculator]');
    if (!calculators.length) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const storageKey = 'katy_shipping_zip';

    const sanitizeZip = (value) => String(value || '').replace(/\D/g, '').slice(0, 8);
    const formatZip = (value) => {
      const digits = sanitizeZip(value);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    };
    const money = (value) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
    const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

    const formatDate = (value) => {
      if (!value) return '';
      const date = new Date(`${value}T12:00:00`);
      if (Number.isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
    };

    const rateDeadline = (rate) => {
      if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
        const days = rate.delivery_days.map(Number).filter((value) => Number.isFinite(value));
        if (days.length) {
          const min = Math.min(...days);
          const max = Math.max(...days);
          if (min === max) return `${max} ${max === 1 ? 'dia útil' : 'dias úteis'}`;
          return `${min}–${max} dias úteis`;
        }
      }

      if (Array.isArray(rate.delivery_range) && rate.delivery_range.length >= 2) {
        const start = formatDate(rate.delivery_range[0]);
        const end = formatDate(rate.delivery_range[1]);
        if (start && end) return `Entrega entre ${start} e ${end}`;
      }

      if (rate.delivery_date) {
        const date = formatDate(rate.delivery_date);
        if (date) return `Entrega prevista em ${date}`;
      }

      if (rate.description) return String(rate.description);
      return '';
    };

    const renderMessage = (target, message, state = 'info') => {
      target.hidden = false;
      target.innerHTML = '';
      const node = document.createElement('p');
      node.className = `shipping-calculator__message is-${state}`;
      node.textContent = message;
      target.appendChild(node);
    };

    const renderRates = (target, rates) => {
      target.hidden = false;
      target.innerHTML = '';
      if (!Array.isArray(rates) || !rates.length) {
        renderMessage(target, 'Nenhuma opção de frete foi encontrada para este CEP.', 'error');
        return;
      }

      const list = document.createElement('div');
      list.className = 'shipping-calculator__rates';

      rates.forEach((rate) => {
        const row = document.createElement('div');
        row.className = 'shipping-calculator__rate';

        const info = document.createElement('div');
        info.className = 'shipping-calculator__rate-info';

        const title = document.createElement('strong');
        title.textContent = rate.presentment_name || rate.name || rate.code || 'Entrega';
        info.appendChild(title);

        const deadline = rateDeadline(rate);
        if (deadline) {
          const deadlineNode = document.createElement('small');
          deadlineNode.className = 'shipping-calculator__deadline';
          deadlineNode.textContent = deadline;
          info.appendChild(deadlineNode);
        }

        const price = document.createElement('span');
        price.className = 'shipping-calculator__rate-price';
        price.textContent = Number(rate.price || 0) === 0 ? 'Grátis' : money(rate.price);

        row.append(info, price);
        list.appendChild(row);
      });

      target.appendChild(list);
    };

    const responseError = async (response, fallback) => {
      try {
        const payload = await response.json();
        const errors = payload && (payload.error || payload.errors);
        if (typeof errors === 'string') return errors;
        if (errors && typeof errors === 'object') {
          const text = Object.values(errors).flat().filter(Boolean).join(' ');
          if (text) return text;
        }
      } catch (_error) {
        // Ignore malformed error responses and use the friendly fallback below.
      }
      return fallback;
    };

    const fetchRatesDirect = async (query) => {
      const response = await fetch(`${root}cart/shipping_rates.json?${query}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error(await responseError(response, 'Não foi possível calcular o frete para este CEP.'));
      }
      const data = await response.json();
      return Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
    };

    const brazilProvinceFromZip = (zip) => {
      const value = Number(String(zip || '').replace(/\D/g, ''));
      if (!Number.isFinite(value)) return '';

      const ranges = [
        [1000000, 19999999, 'SP'],
        [20000000, 28999999, 'RJ'],
        [29000000, 29999999, 'ES'],
        [30000000, 39999999, 'MG'],
        [40000000, 48999999, 'BA'],
        [49000000, 49999999, 'SE'],
        [50000000, 56999999, 'PE'],
        [57000000, 57999999, 'AL'],
        [58000000, 58999999, 'PB'],
        [59000000, 59999999, 'RN'],
        [60000000, 63999999, 'CE'],
        [64000000, 64999999, 'PI'],
        [65000000, 65999999, 'MA'],
        [66000000, 68899999, 'PA'],
        [68900000, 68999999, 'AP'],
        [69000000, 69299999, 'AM'],
        [69300000, 69399999, 'RR'],
        [69400000, 69899999, 'AM'],
        [69900000, 69999999, 'AC'],
        [70000000, 72799999, 'DF'],
        [72800000, 72999999, 'GO'],
        [73000000, 73699999, 'DF'],
        [73700000, 76799999, 'GO'],
        [76800000, 76999999, 'RO'],
        [77000000, 77999999, 'TO'],
        [78000000, 78899999, 'MT'],
        [79000000, 79999999, 'MS'],
        [80000000, 87999999, 'PR'],
        [88000000, 89999999, 'SC'],
        [90000000, 99999999, 'RS'],
      ];

      const match = ranges.find(([start, end]) => value >= start && value <= end);
      return match ? match[2] : '';
    };

    const fetchRates = async (zip) => {
      const params = new URLSearchParams();
      const province = brazilProvinceFromZip(zip);
      params.set('shipping_address[zip]', zip);
      params.set('shipping_address[country]', 'BR');
      if (province) params.set('shipping_address[province]', province);
      const query = params.toString();

      const prepare = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });

      if (!prepare.ok) {
        // Some stores/carriers can reject the prepare endpoint while the direct
        // shipping_rates endpoint still returns the checkout rates correctly.
        return fetchRatesDirect(query);
      }

      for (let attempt = 0; attempt < 14; attempt += 1) {
        if (attempt > 0) await wait(attempt < 5 ? 400 : 650);
        const response = await fetch(`${root}cart/async_shipping_rates.json?${query}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });

        if (!response.ok) {
          if (response.status === 422) break;
          throw new Error(await responseError(response, 'Não foi possível calcular o frete.'));
        }

        const data = await response.json();
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      }

      // Official Shopify fallback. This endpoint is more synchronous and can be
      // throttled, so it is used only when the recommended async flow does not finish.
      return fetchRatesDirect(query);
    };

    // V75 — cálculo de frete na página de produto.
    // A Shopify só devolve fretes para um carrinho com itens. Quando o carrinho está vazio,
    // a cor selecionada entra por um instante (com a marca oculta _estimativa_frete), o frete é
    // consultado e a linha é removida em seguida. Se o carrinho já tem itens, ele é usado como está.
    const tempLineKey = 'katy_shipping_temp_line';
    const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

    const readCart = async () => {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Não foi possível consultar o carrinho.');
      return response.json();
    };

    const removeLine = async (key) => {
      if (!key) return false;
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: jsonHeaders,
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        return response.ok;
      } catch (_error) {
        return false;
      }
    };

    const forgetTempLine = () => {
      try {
        window.sessionStorage.removeItem(tempLineKey);
      } catch (_error) {
        // sessionStorage may be unavailable.
      }
    };

    const removeEstimateLines = async (cart) => {
      const leftovers = (cart.items || []).filter((item) => item.properties && item.properties._estimativa_frete);
      let removed = 0;
      for (const item of leftovers) {
        if (await removeLine(item.key)) removed += 1;
      }
      forgetTempLine();
      return removed;
    };

    const selectedVariantId = () => {
      const checked = document.querySelector('[data-product-form] input[name="id"]:checked');
      const hidden = document.querySelector('[data-product-form] input[type="hidden"][name="id"]');
      const button = document.querySelector('[data-add-to-cart]');
      return Number((checked && checked.value) || (hidden && hidden.value) || (button && button.dataset.variantId) || 0);
    };

    const fetchRatesForProduct = async (zip) => {
      let cart = await readCart();
      if ((cart.items || []).some((item) => item.properties && item.properties._estimativa_frete)) {
        await removeEstimateLines(cart);
        cart = await readCart();
      }

      if (cart.item_count > 0) {
        return { rates: await fetchRates(zip), basis: 'cart' };
      }

      const variantId = selectedVariantId();
      if (!variantId) throw new Error('Escolha uma cor disponível para calcular o frete.');

      const add = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _estimativa_frete: '1' } }] }),
      });
      if (!add.ok) {
        throw new Error(await responseError(add, 'Escolha uma cor disponível para calcular o frete.'));
      }

      const added = await add.json();
      const key = added && added.items && added.items[0] ? added.items[0].key : '';
      try {
        if (key) window.sessionStorage.setItem(tempLineKey, key);
      } catch (_error) {
        // Continue without the safety marker.
      }

      try {
        return { rates: await fetchRates(zip), basis: 'product' };
      } finally {
        const removed = await removeLine(key);
        if (removed) forgetTempLine();
      }
    };

    const renderNote = (target, message) => {
      const note = document.createElement('p');
      note.className = 'shipping-calculator__note';
      note.textContent = message;
      target.appendChild(note);
    };

    // Rede de segurança: se uma consulta anterior foi interrompida (aba fechada no meio),
    // remove a linha temporária na próxima página aberta.
    (async () => {
      let pending = '';
      try {
        pending = window.sessionStorage.getItem(tempLineKey) || '';
      } catch (_error) {
        pending = '';
      }
      const onCartPage = Boolean(document.querySelector('[data-shipping-context="cart"]'));
      if (!pending && !onCartPage) return;
      try {
        const cart = await readCart();
        const removed = await removeEstimateLines(cart);
        if (removed > 0 && onCartPage) window.location.reload();
      } catch (_error) {
        // Nothing else to do here.
      }
    })();

    // V79: "Failed to fetch" aparecia na tela quando a conexão falhava por um instante ou quando a página
    // recarregava no meio do cálculo (o carrinho recalcula sozinho ao abrir, e o app de lentes às vezes
    // recarrega a página). Agora: página saindo = nada aparece; falha de rede = tenta de novo uma vez e,
    // se falhar outra vez, mostra uma frase em português.
    let pageLeaving = false;
    window.addEventListener('pagehide', () => {
      pageLeaving = true;
    });
    window.addEventListener('beforeunload', () => {
      pageLeaving = true;
    });
    const isNetworkError = (error) =>
      error instanceof TypeError ||
      /failed to fetch|load failed|networkerror|network request failed|aborted/i.test(String((error && error.message) || ''));

    calculators.forEach((calculator) => {
      const input = calculator.querySelector('[data-shipping-zip]');
      const button = calculator.querySelector('[data-shipping-submit]');
      const results = calculator.querySelector('[data-shipping-results]');
      if (!input || !button || !results) return;

      try {
        const saved = window.localStorage.getItem(storageKey);
        if (saved) input.value = formatZip(saved);
      } catch (_error) {
        // localStorage may be unavailable.
      }

      input.addEventListener('input', () => {
        input.value = formatZip(input.value);
      });

      const run = async (isRetry = false) => {
        const zip = sanitizeZip(input.value);
        if (zip.length !== 8) {
          renderMessage(results, 'Digite um CEP válido com 8 números.', 'error');
          return;
        }

        try {
          window.localStorage.setItem(storageKey, zip);
        } catch (_error) {
          // Continue without persistence.
        }

        button.disabled = true;
        button.textContent = 'Calculando...';
        renderMessage(results, 'Calculando...', 'info');

        try {
          if (calculator.dataset.shippingContext === 'product') {
            const estimate = await fetchRatesForProduct(zip);
            renderRates(results, estimate.rates);
            // V81: avisa o bloco "Quando chega" da página de produto para mostrar a data.
            document.dispatchEvent(new CustomEvent('katy:shipping-rates', { detail: { zip, rates: estimate.rates || [], context: 'product' } }));
            if (Array.isArray(estimate.rates) && estimate.rates.length) {
              renderNote(
                results,
                estimate.basis === 'cart'
                  ? 'Calculado com os itens que já estão no seu carrinho. O prazo de entrega conta depois da produção das lentes.'
                  : 'Estimativa para esta armação. O prazo de entrega conta depois da produção das lentes; o valor final aparece no carrinho.',
              );
            }
            return;
          }

          const rates = await fetchRates(zip);
          renderRates(results, rates);
        } catch (error) {
          if (pageLeaving) return;
          if (isNetworkError(error)) {
            if (!isRetry) {
              await wait(900);
              if (!pageLeaving) await run(true);
              return;
            }
            renderMessage(results, 'Não conseguimos calcular o frete agora. Confira a internet e toque em Calcular de novo.', 'error');
            return;
          }
          renderMessage(results, error.message || 'Não foi possível calcular o frete agora.', 'error');
        } finally {
          button.disabled = false;
          button.textContent = 'Calcular';
        }
      };

      button.addEventListener('click', () => run());
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          run();
        }
      });

      if (calculator.dataset.shippingContext === 'cart' && sanitizeZip(input.value).length === 8) {
        run();
      }
    });
  }

  bindMobileNav() {
    const nav = document.querySelector('.mobile-nav');
    if (!nav) return;

    const sync = () => document.body.classList.toggle('has-mobile-nav-open', nav.open);
    nav.addEventListener('toggle', sync);

    document.addEventListener('click', (event) => {
      if (nav.open && !nav.contains(event.target)) nav.open = false;
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && nav.open) nav.open = false;
    });
  }

  bindCollectionSort() {
    document.querySelectorAll('[data-collection-sort]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        if (select.value) {
          url.searchParams.set('sort_by', select.value);
        } else {
          url.searchParams.delete('sort_by');
        }
        url.searchParams.delete('page');
        window.location.assign(url.toString());
      });
    });
  }

  bindCartRemoval() {
    document.querySelectorAll('[data-cart-remove]').forEach((button) => {
      button.addEventListener('click', async () => {
        const line = Number(button.dataset.line || 0);
        // A chave do item não muda se outro item sair da lista; o número da linha muda.
        const key = button.dataset.key || '';
        if ((!line && !key) || button.disabled) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = 'Removendo...';

        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const response = await fetch(`${root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
            body: JSON.stringify(key ? { id: key, quantity: 0 } : { line, quantity: 0 }),
          });

          if (!response.ok) {
            throw new Error('Não foi possível remover o produto.');
          }

          await response.json();
          window.location.assign(`${root}cart`);
        } catch (error) {
          console.error('Erro ao remover item do carrinho:', error);
          button.disabled = false;
          button.textContent = originalLabel;
        }
      });
    });
  }

  // V81: "Quando chega" na página de produto. Depois do cálculo de frete, soma os dias úteis de produção
  // (Configurações do tema) ao maior prazo da opção de frete mais rápida e mostra a data.
  bindArrival() {
    const box = document.querySelector('[data-katy-arrival]');
    if (!box) return;
    const title = box.querySelector('[data-arrival-title]');
    const text = box.querySelector('[data-arrival-text]');
    const cepButton = box.querySelector('[data-arrival-cep]');
    const production = Math.max(0, Number(box.dataset.productionDays || 0)) || 0;

    if (cepButton) {
      cepButton.addEventListener('click', () => {
        const input = document.querySelector('[data-shipping-context="product"] [data-shipping-zip]');
        if (!input) return;
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => input.focus({ preventScroll: true }), 350);
      });
    }

    const addBusinessDays = (start, days) => {
      const date = new Date(start.getTime());
      let left = days;
      while (left > 0) {
        date.setDate(date.getDate() + 1);
        const day = date.getDay();
        if (day !== 0 && day !== 6) left -= 1;
      }
      return date;
    };
    const fmt = (date) => new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' }).format(date).replace('.', '');

    document.addEventListener('katy:shipping-rates', (event) => {
      const detail = event.detail || {};
      const rates = Array.isArray(detail.rates) ? detail.rates : [];
      let best = null;
      rates.forEach((rate) => {
        let end = null;
        const days = Array.isArray(rate.delivery_days) ? rate.delivery_days.map(Number).filter(Number.isFinite) : [];
        if (days.length) {
          end = addBusinessDays(addBusinessDays(new Date(), production), Math.max(...days));
        } else if (Array.isArray(rate.delivery_range) && rate.delivery_range[1]) {
          const base = new Date(`${rate.delivery_range[1]}T12:00:00`);
          if (!Number.isNaN(base.getTime())) end = addBusinessDays(base, production);
        }
        if (end && (!best || end < best.end)) best = { end, name: rate.name || rate.presentment_name || '' };
      });
      if (!best || !title || !text) return;
      const zip = String(detail.zip || '').replace(/^(\d{5})(\d{3})$/, '$1-$2');
      title.textContent = `Chega até ${fmt(best.end)}`;
      title.classList.add('is-date');
      text.textContent = `Para o CEP ${zip}${best.name ? `, com ${best.name}` : ''}. Já conta ${production} ${production === 1 ? 'dia útil' : 'dias úteis'} de produção das lentes.`;
    });
  }

  // V81: vídeos em fila. Tocam mudos; tocar no vídeo pausa ou volta a tocar.
  bindVideoRow() {
    document.querySelectorAll('[data-pvideos] video').forEach((video) => {
      video.addEventListener('click', () => { if (video.paused) video.play().catch(() => {}); else video.pause(); });
    });
  }

  // V81: cronômetro do carrinho. Começa na primeira visita ao carrinho, guarda o fim no navegador
  // (não reinicia ao recarregar), some no zero e só volta 24 h depois. Carrinho vazio limpa o relógio.
  bindCartTimer() {
    const key = 'katy_cart_timer_end';
    const read = () => { try { return Number(window.localStorage.getItem(key) || 0); } catch (_e) { return 0; } };
    const write = (value) => { try { if (value) window.localStorage.setItem(key, String(value)); else window.localStorage.removeItem(key); } catch (_e) { /* sem armazenamento */ } };
    if (document.querySelector('[data-cart-timer-reset]')) write(0);
    const box = document.querySelector('[data-cart-timer]');
    if (!box) return;
    const clock = box.querySelector('[data-cart-timer-clock]');
    const minutes = Math.max(1, Number(box.dataset.minutes || 20));
    const now = Date.now();
    let end = read();
    if (!end || end > now + minutes * 60000 || now - end > 24 * 3600000) {
      end = now + minutes * 60000;
      write(end);
    }
    const pad = (value) => String(value).padStart(2, '0');
    let timer = 0;
    const render = () => {
      const left = end - Date.now();
      if (left <= 0) {
        box.hidden = true;
        if (timer) window.clearInterval(timer);
        return;
      }
      const total = Math.ceil(left / 1000);
      if (clock) clock.textContent = `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
    };
    render();
    if (!box.hidden) timer = window.setInterval(render, 1000);
  }

  // V81: cronômetro da promoção com prazo real (Configurações do tema > Oferta do mês > Fim da promoção).
  // Aceita "2026-10-31 23:59", "2026-10-31" ou "31/10/2026 23:59", sempre no horário de Brasília (UTC-3).
  // Nunca reinicia: quando o prazo passa, o cronômetro some.
  parseDeadline(raw) {
    const text = String(raw || '').trim();
    let m = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
    let y; let mo; let d; let h; let mi;
    if (m) { [, y, mo, d, h, mi] = m; } else {
      m = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(?:às\s+)?(\d{1,2})[:h](\d{2}))?/);
      if (!m) return NaN;
      [, d, mo, y, h, mi] = m;
    }
    const hasTime = h !== undefined;
    const hour = hasTime ? Number(h) : 23;
    const minute = hasTime ? Number(mi) : 59;
    const second = hasTime ? 0 : 59;
    if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31 || hour > 23 || minute > 59) return NaN;
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), hour + 3, minute, second);
  }

  bindCartCountdown() {
    const blocks = Array.from(document.querySelectorAll('[data-katy-countdown]'));
    if (!blocks.length) return;
    const items = blocks
      .map((block) => ({ block, end: this.parseDeadline(block.dataset.deadline), nodes: {
        d: block.querySelector('[data-cd="d"]'), h: block.querySelector('[data-cd="h"]'),
        m: block.querySelector('[data-cd="m"]'), s: block.querySelector('[data-cd="s"]'),
      } }))
      .filter((item) => Number.isFinite(item.end));
    const pad = (value) => String(value).padStart(2, '0');
    let timer = 0;
    const render = () => {
      const now = Date.now();
      let active = 0;
      items.forEach((item) => {
        const remaining = item.end - now;
        if (remaining <= 0) { item.block.hidden = true; return; }
        active += 1;
        const total = Math.floor(remaining / 1000);
        if (item.nodes.d) item.nodes.d.textContent = pad(Math.floor(total / 86400));
        if (item.nodes.h) item.nodes.h.textContent = pad(Math.floor((total % 86400) / 3600));
        if (item.nodes.m) item.nodes.m.textContent = pad(Math.floor((total % 3600) / 60));
        if (item.nodes.s) item.nodes.s.textContent = pad(total % 60);
        item.block.hidden = false;
      });
      if (!active && timer) window.clearInterval(timer);
    };
    render();
    if (items.some((item) => item.end > Date.now())) timer = window.setInterval(render, 1000);
  }

  // V78: carrinho novo. Quantidade com − e +, cupom (copiar e remover), parcela que acompanha o total
  // e barra fixa do celular que some quando o botão principal está na tela.
  bindCartPage() {
    const cartRoot = document.querySelector('.kcart');
    if (!cartRoot) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const postJSON = (path, payload) =>
      fetch(`${root}${path}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

    // Quantidade: grava na hora e recarrega, para o total nunca ficar diferente do que está na tela.
    cartRoot.querySelectorAll('[data-cart-qty]').forEach((box) => {
      const input = box.querySelector('[data-cart-qty-input]');
      const line = Number(box.dataset.line || 0);
      const key = box.dataset.key || '';
      if (!input || (!line && !key)) return;

      const initial = Math.max(1, Number(input.value) || 1);
      const controls = Array.from(box.querySelectorAll('button, input'));
      const minus = box.querySelector('[data-cart-qty-step="-1"]');
      let busy = false;

      const setBusy = (state) => {
        busy = state;
        box.classList.toggle('is-busy', state);
        controls.forEach((control) => {
          if (control === input) control.readOnly = state;
          else control.disabled = state;
        });
        // Enquanto a quantidade é gravada, finalizar fica em espera: senão o clique e a recarga disputam.
        cartRoot.querySelectorAll('button[name="checkout"]').forEach((button) => {
          button.disabled = state;
        });
        if (!state && minus) minus.disabled = initial <= 1;
      };

      const showError = (message) => {
        const host = box.closest('.kcart-item__controls') || box.parentNode;
        let note = host.parentNode.querySelector('.kcart-item__error');
        if (!note) {
          note = document.createElement('p');
          note.className = 'kcart-item__error';
          note.setAttribute('role', 'alert');
          host.parentNode.appendChild(note);
        }
        note.textContent = message;
      };

      const commit = async (value) => {
        if (busy) return;
        const next = Math.max(1, Math.min(99, Math.round(Number(value) || initial)));
        if (next === initial) {
          input.value = String(initial);
          return;
        }

        input.value = String(next);
        setBusy(true);

        try {
          const response = await postJSON('cart/change.js', key ? { id: key, quantity: next } : { line, quantity: next });
          if (!response.ok) {
            const error = new Error('quantity');
            error.status = response.status;
            throw error;
          }
          await response.json();
          window.location.assign(`${root}cart`);
        } catch (error) {
          // Sem código de resposta, a troca pode ter sido gravada (o app de lentes revalida o carrinho
          // depois dela). Recarregar mostra o que de fato ficou gravado.
          if (error && error.status === undefined && navigator.onLine !== false) {
            window.location.assign(`${root}cart`);
            return;
          }
          input.value = String(initial);
          setBusy(false);
          showError(
            error && error.status === 422
              ? 'Não há mais unidades disponíveis deste item.'
              : 'Não foi possível mudar a quantidade. Tente de novo.',
          );
        }
      };

      box.querySelectorAll('[data-cart-qty-step]').forEach((button) => {
        button.addEventListener('click', () => commit(initial + Number(button.dataset.cartQtyStep || 0)));
      });
      input.addEventListener('change', () => commit(input.value));
      input.addEventListener('keydown', (event) => {
        // Enter dentro do formulário enviaria o carrinho para o checkout.
        if (event.key === 'Enter') {
          event.preventDefault();
          commit(input.value);
        }
      });
    });

    // Cupom: o tema só mostra o código. Quem decide usar é o cliente.
    cartRoot.querySelectorAll('[data-kcart-coupon-copy]').forEach((button) => {
      button.addEventListener('click', async () => {
        const code = button.dataset.code || '';
        const coupon = button.closest('[data-kcart-coupon]');
        const message = coupon ? coupon.querySelector('[data-kcart-coupon-message]') : null;
        const codeNode = coupon ? coupon.querySelector('.kcart-coupon__code') : null;
        const original = button.dataset.label || button.textContent.trim();
        button.dataset.label = original;

        try {
          await navigator.clipboard.writeText(code);
          button.textContent = 'Copiado';
          if (message) message.textContent = 'Código copiado. Cole no campo de cupom da próxima etapa.';
        } catch (_error) {
          if (codeNode && window.getSelection && document.createRange) {
            const range = document.createRange();
            range.selectNodeContents(codeNode);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          }
          if (message) message.textContent = 'Selecione o código acima e copie.';
        }

        window.setTimeout(() => {
          button.textContent = original;
        }, 2200);
      });
    });

    cartRoot.querySelectorAll('[data-kcart-coupon-remove]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'Removendo...';

        try {
          const response = await postJSON('cart/update.js', { discount: '' });
          if (!response.ok) throw new Error('discount');
          await response.json();
          window.location.assign(`${root}cart`);
        } catch (_error) {
          button.disabled = false;
          button.textContent = original;
        }
      });
    });

    // O app de lentes pode reescrever o total (lente inclusa). A parcela acompanha o que estiver escrito.
    const total = cartRoot.querySelector('[data-kcart-total]');
    const installments = cartRoot.querySelector('[data-kcart-installments]');
    if (total && installments && 'MutationObserver' in window) {
      const money = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
      const syncInstallments = () => {
        const digits = (total.textContent || '').replace(/[^\d,]/g, '').replace(',', '.');
        const value = Number(digits);
        if (!digits || !Number.isFinite(value) || value <= 0) return;
        const text = `até 6x de ${money(Math.floor(Math.round(value * 100) / 6) / 100)} sem juros`;
        if (installments.textContent !== text) installments.textContent = text;
      };
      new MutationObserver(syncInstallments).observe(total, { childList: true, characterData: true, subtree: true });
    }

    // Barra fixa do celular: aparece só enquanto o botão principal não está na tela.
    const sticky = cartRoot.querySelector('[data-kcart-sticky]');
    const mainButton = cartRoot.querySelector('[data-kcart-checkout]');
    if (sticky && mainButton && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => sticky.classList.toggle('is-hidden', entry.isIntersecting));
        },
        { threshold: 0.6 },
      ).observe(mainButton);
    }

    // Resumo fixo no computador: desce logo abaixo do cabeçalho, que também é fixo.
    // Resumo fixo no computador só quando ele cabe inteiro na tela; senão o botão de finalizar ficaria cortado.
    const side = cartRoot.querySelector('.kcart-side');
    const setSummarySticky = () => {
      if (side) side.classList.toggle('is-sticky', side.offsetHeight + 36 <= window.innerHeight);
    };
    setSummarySticky();
    window.addEventListener('resize', setSummarySticky);
    if (side && 'ResizeObserver' in window) new ResizeObserver(setSummarySticky).observe(side);

    // Quando o app de lentes segura o checkout, ele escreve o motivo no topo da lista de itens.
    // No celular isso fica fora da tela; a página sobe até o aviso para o cliente ver por que não avançou.
    const itemsList = cartRoot.querySelector('.cart-items');
    if (itemsList && 'MutationObserver' in window) {
      let lastNotice = '';
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      new MutationObserver(() => {
        const notice = itemsList.querySelector('#app-lenses-cart-promotion-notice');
        // O app de lentes mostra o erro técnico do navegador ("Failed to fetch") quando a conexão falha.
        if (notice && /^(typeerror:\s*)?(failed to fetch|load failed|networkerror[^.]*\.?|network request failed)$/i.test(notice.textContent.trim())) {
          notice.textContent = 'Não conseguimos atualizar o carrinho agora. Confira a internet e toque em Finalizar compra de novo.';
        }
        const text = notice ? notice.textContent.trim() : '';
        if (!text) {
          lastNotice = '';
          return;
        }
        if (text === lastNotice) return;
        lastNotice = text;
        const position = notice.getBoundingClientRect();
        if (position.top < 0 || position.bottom > window.innerHeight) {
          notice.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }).observe(itemsList, { childList: true, subtree: true, characterData: true });
    }
  }

  bindCartUpsell() {
    document.querySelectorAll('[data-cart-upsell-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const variantId = Number(button.dataset.variantId || 0);
        if (!variantId || button.disabled) return;

        const originalLabel = button.textContent;
        button.disabled = true;
        button.textContent = 'Adicionando...';

        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('Não foi possível adicionar o produto.');
          window.location.reload();
        } catch (_error) {
          button.disabled = false;
          button.textContent = 'Tentar novamente';
          window.setTimeout(() => {
            button.textContent = originalLabel;
          }, 2200);
        }
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new KatyTheme());
